import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkle, ArrowRight, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function SubscribePage() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState(null);
  const [checkingOut, setCheckingOut] = useState(null);
  const [polling, setPolling] = useState(false);
  const [pollResult, setPollResult] = useState(null);
  const [recovering, setRecovering] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [upgrade, setUpgrade] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [checkingCode, setCheckingCode] = useState(false);
  const sessionId = params.get("session_id");
  const success = window.location.pathname.endsWith("/success");

  useEffect(() => {
    api.get("/subscription/plans").then((r) => setPlans(r.data.plans)).catch(() => {});
    api.get("/me/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  // Poll on success return
  useEffect(() => {
    if (!success || !sessionId) return;
    setPolling(true);
    let attempts = 0;
    const maxAttempts = 12;
    const iv = setInterval(async () => {
      attempts++;
      try {
        const { data } = await api.get(`/subscription/status/${sessionId}`);
        if (data.payment_status === "paid") {
          clearInterval(iv);
          setPolling(false);
          setPollResult("paid");
          refresh?.();
          toast.success("Welcome back to the practice.");
        } else if (data.payment_status === "failed" || attempts >= maxAttempts) {
          clearInterval(iv);
          setPolling(false);
          setPollResult(data.payment_status === "failed" ? "failed" : "timeout");
        }
      } catch (err) {
        if (attempts >= maxAttempts) {
          clearInterval(iv);
          setPolling(false);
          setPollResult("timeout");
        }
      }
    }, 2500);
    return () => clearInterval(iv);
  }, [success, sessionId, refresh]);

  const recoverPayment = async () => {
    setRecovering(true);
    try {
      const { data } = await api.post("/subscription/reconcile");
      if (data.recovered > 0) {
        refresh?.();
        setPollResult("paid");
        toast.success("Found your payment. Your access is open.");
      } else {
        toast.error("No completed payment found on this account yet.");
      }
    } catch (err) {
      toast.error("Couldn’t check with Stripe just now. Please try again.");
    } finally {
      setRecovering(false);
    }
  };

  // If a payment was taken but never applied, quietly put it right on arrival.
  useEffect(() => {
    if (!user || user.subscription_status === "active") return;
    api.post("/subscription/reconcile").then(({ data }) => {
      if (data.recovered > 0) {
        refresh?.();
        toast.success("Found your payment. Your access is open.");
      }
    }).catch(() => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const canUpgrade = user?.plan === "monthly" && user?.has_subscription && !user?.plan_cancelled;

  useEffect(() => {
    if (!canUpgrade) return;
    api.get("/subscription/upgrade-preview").then((r) => setUpgrade(r.data)).catch(() => {});
  }, [canUpgrade]);

  const switchToAnnual = async () => {
    setUpgrading(true);
    try {
      const { data } = await api.post("/subscription/upgrade");
      await refresh?.();
      setUpgrade(null);
      toast.success(
        data.trialing
          ? "You're on the yearly plan. Still nothing charged until your free days are up."
          : "You're on the yearly plan. We only charged the difference."
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not switch the plan just now.");
    } finally {
      setUpgrading(false);
    }
  };

  const startCheckout = async (planKey) => {
    setCheckingOut(planKey);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/subscription/checkout", {
        plan: planKey,
        origin_url: origin,
        code: planKey === "annual" && promo?.valid ? promo.code : null,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open checkout");
      setCheckingOut(null);
    }
  };

  const applyCode = async () => {
    const entered = codeInput.trim();
    if (!entered) return;
    setCheckingCode(true);
    try {
      const { data } = await api.post("/subscription/check-code", { plan: "annual", code: entered });
      if (data.valid) {
        setPromo(data);
        toast.success(data.blurb || "Code applied.");
      } else {
        setPromo(null);
        toast.error(data.error || "That code isn't valid.");
      }
    } catch (err) {
      setPromo(null);
      toast.error("Couldn't check that code just now.");
    } finally {
      setCheckingCode(false);
    }
  };

  // Success screen
  if (success) {
    return (
      <div className="min-h-full flex items-center justify-center px-5 py-16" data-testid="subscribe-success">
        <div className="max-w-lg text-center">
          {polling && (
            <>
              <div className="w-12 h-12 mx-auto rounded-full border-2 border-clay border-t-transparent animate-spin mb-6" />
              <p className="font-serif-display italic text-2xl text-ink">Confirming your choice…</p>
            </>
          )}
          {pollResult === "paid" && (
            <>
              <div className="w-16 h-16 mx-auto rounded-full bg-clay/20 grid place-items-center mb-6">
                <Check size={28} weight="thin" className="text-clay" />
              </div>
              <h1 className="font-serif-display text-4xl text-ink mb-4">You’re in.</h1>
              <p className="text-ink-soft leading-relaxed mb-8">
                The mirror is yours to keep. Your first 14 days are free, so nothing is charged until then. After that it renews on its own, so nothing interrupts you. You can cancel any time in Settings.
              </p>
              <button
                onClick={() => nav("/app")}
                className="rounded-full bg-ink text-canvas px-7 py-3 text-sm font-medium hover:bg-ink/90 transition-colors"
                data-testid="success-continue"
              >
                Back to writing
              </button>
            </>
          )}
          {(pollResult === "timeout" || pollResult === "failed") && (
            <>
              <p className="font-serif-display italic text-2xl text-ink mb-4">
                {pollResult === "failed" ? "The payment didn’t complete." : "Still confirming…"}
              </p>
              <p className="text-ink-soft mb-6">
                If Stripe charged your card, nothing is lost. Check again and we’ll open your access.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <button
                  onClick={() => recoverPayment()}
                  disabled={recovering}
                  className="rounded-full bg-ink text-canvas px-6 py-2.5 text-sm font-medium hover:bg-ink/90 transition-colors disabled:opacity-50"
                  data-testid="recover-payment-btn"
                >
                  {recovering ? "Checking with Stripe…" : "I paid, check again"}
                </button>
                <button
                  onClick={() => nav("/app/subscribe")}
                  className="rounded-full border border-line-strong px-6 py-2.5 text-sm hover:bg-canvas-warm transition-colors"
                  data-testid="success-try-again"
                >
                  Back to plans
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  const trialing = user?.subscription_status === "trialing";
  const active = user?.subscription_status === "active";
  const lapsed = user?.subscription_status === "lapsed";
  // Someone with a one-time period left, or a cancelled subscription, should still be able to subscribe.
  const autoRenewing = active && !!user?.auto_renew;
  const carryOverDays = active && !autoRenewing
    ? Math.max(0, Math.ceil((new Date(user.access_until) - new Date()) / 86400000))
    : 0;

  let headline = "Continue with the practice.";
  if (autoRenewing) headline = "You're already writing with us.";
  else if (active) headline = "Make it automatic.";
  else if (lapsed) headline = "Keep your story going.";

  return (
    <div className="px-5 sm:px-10 md:px-20 py-10 sm:py-14 max-w-4xl mx-auto" data-testid="subscribe-page">
      <p className="text-xs font-semibold uppercase tracking-[0.3em] text-ink-mute">Stay with it</p>
      <h1 className="font-serif-display text-3xl sm:text-4xl md:text-5xl text-ink mt-3 mb-4 leading-tight">
        {headline}
      </h1>
      <p className="text-ink-soft max-w-2xl leading-relaxed text-base sm:text-lg mb-4">
        {autoRenewing
          ? "Thank you for keeping the mirror alive. Below is your current plan."
          : carryOverDays > 0
          ? `You already have ${carryOverDays} day${carryOverDays === 1 ? "" : "s"} of access left, and none of it is lost. A subscription simply takes over when it ends, so your writing never stops mid-sentence.`
          : "Your first 14 days are free, and your card isn’t touched until they’re up. After that it renews on its own, so your entries, your patterns and your growing library of self-knowledge simply carry on."}
      </p>
      {!autoRenewing && (
        <p className="text-sm text-ink-mute mb-10" data-testid="renewal-disclosure">
          Card saved securely by Stripe ·{" "}
          {carryOverDays > 0
            ? `nothing charged until ${new Date(user.access_until).toLocaleDateString(undefined, { month: "long", day: "numeric" })}`
            : "nothing charged for 14 days"}{" "}
          · renews automatically until you cancel · cancel any time in Settings
        </p>
      )}

      {/* Stats card */}
      {stats && stats.entries > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-10 rounded-3xl bg-canvas-warm border border-line p-6 sm:p-8"
          data-testid="subscribe-stats"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-ink-mute mb-4">What you’d keep</p>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="font-serif-display text-4xl sm:text-5xl text-clay">{stats.entries}</p>
              <p className="text-sm text-ink-soft mt-1">{stats.entries === 1 ? "entry written" : "entries written"}</p>
            </div>
            <div>
              <p className="font-serif-display text-4xl sm:text-5xl text-clay">{stats.days_journaled}</p>
              <p className="text-sm text-ink-soft mt-1">{stats.days_journaled === 1 ? "day of noticing" : "days of noticing"}</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Monthly → yearly */}
      {canUpgrade && upgrade && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-10 rounded-3xl bg-ink text-canvas p-6 sm:p-8"
          data-testid="upgrade-card"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-canvas/60 mb-3">A quieter year</p>
          <h2 className="font-serif-display text-2xl md:text-3xl mb-3">Move to the yearly plan</h2>
          <p className="text-canvas/85 text-sm sm:text-base leading-relaxed mb-2" data-testid="upgrade-summary">
            {upgrade.trialing ? (
              <>
                You’re still inside your free days, so nothing is charged today. Switch now and your first charge
                becomes <b>${upgrade.annual_amount.toFixed(2)} for the whole year</b> instead of ${(5).toFixed(2)} a month.
              </>
            ) : (
              <>
                You’d pay <b data-testid="upgrade-due">${upgrade.due_today.toFixed(2)} today</b>: the difference only.
                {upgrade.credit > 0 && <> We credit the <b>${upgrade.credit.toFixed(2)}</b> you’ve already paid for the rest of this month.</>}
                {" "}After that it’s <b>${upgrade.annual_amount.toFixed(2)} a year</b>, renewing automatically.
              </>
            )}
          </p>
          <p className="text-canvas/60 text-xs mb-6">
            Works out at ${(upgrade.annual_amount / 12).toFixed(2)} a month · cancel any time in Settings
          </p>
          <button
            onClick={switchToAnnual}
            disabled={upgrading}
            data-testid="upgrade-btn"
            className="rounded-full bg-canvas text-ink px-7 py-3 text-sm font-medium hover:bg-canvas/90 transition-colors disabled:opacity-60 inline-flex items-center gap-2"
          >
            {upgrading ? "One moment…" : "Switch to yearly"}
            {!upgrading && <ArrowRight size={16} weight="bold" />}
          </button>
        </motion.div>
      )}

      {/* Plans */}
      {!autoRenewing && (
        <>
          <div className="mb-8 rounded-3xl bg-canvas-warm border border-line p-5 sm:p-6" data-testid="promo-box">
            <label htmlFor="promo-code" className="block text-xs uppercase tracking-[0.25em] text-ink-mute mb-3">
              Have a code?
            </label>
            <div className="flex flex-wrap gap-3">
              <input
                id="promo-code"
                data-testid="promo-code-input"
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && applyCode()}
                placeholder="Enter your code"
                className="flex-1 min-w-[160px] rounded-full border border-line-strong bg-canvas px-5 py-2.5 text-sm text-ink font-mono tracking-wide placeholder:font-sans placeholder:tracking-normal placeholder:text-ink-mute focus:outline-none focus:border-clay transition-colors"
              />
              <button
                onClick={applyCode}
                disabled={checkingCode || !codeInput.trim()}
                data-testid="promo-apply-btn"
                className="rounded-full bg-ink text-canvas px-6 py-2.5 text-sm font-medium hover:bg-ink/90 transition-colors disabled:opacity-40"
              >
                {checkingCode ? "Checking…" : "Apply"}
              </button>
            </div>
            {user?.locked_annual_price && !promo?.valid && (
              <p className="text-sm text-clay-dark mt-3" data-testid="locked-price-note">
                Your code is already on this account, so the yearly plan is <b>${user.locked_annual_price.toFixed(2)}</b> for you, forever. No need to type it again.
              </p>
            )}
            {promo?.valid && (
              <p className="text-sm text-clay-dark mt-3" data-testid="promo-applied">
                <b>{promo.code}</b> applied. {promo.blurb} Annual is now{" "}
                <b>${promo.amount.toFixed(2)}</b> instead of ${promo.full_amount.toFixed(2)}
                {promo.renews_at != null && (
                  <>, and it renews at <b>${promo.renews_at.toFixed(2)}/year</b></>
                )}
                .
              </p>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-5 mb-10" data-testid="subscribe-plans">
            {plans.map((p) => {
              const locked = p.key === "annual" && !promo?.valid && user?.locked_annual_price;
              let shown = p;
              if (p.key === "annual" && promo?.valid) {
                shown = { ...p, amount: promo.amount, per_month: promo.amount / 12, was: p.amount };
              } else if (locked) {
                shown = {
                  ...p,
                  amount: user.locked_annual_price,
                  per_month: user.locked_annual_price / 12,
                  was: p.amount,
                  note: "Your locked-in price, yours for as long as you keep writing",
                };
              }
              return (
                <PlanCard
                  key={p.key}
                  plan={shown}
                  onSelect={() => startCheckout(p.key)}
                  loading={checkingOut === p.key}
                  disabled={!!checkingOut}
                />
              );
            })}
          </div>
        </>
      )}

      {!autoRenewing && (
        <p className="text-sm text-ink-mute mb-10 -mt-4">
          Already paid and still seeing this?{" "}
          <button
            onClick={() => recoverPayment()}
            disabled={recovering}
            className="text-clay hover:text-clay-dark underline underline-offset-4 disabled:opacity-50"
            data-testid="already-paid-btn"
          >
            {recovering ? "Checking with Stripe…" : "Restore my access"}
          </button>
        </p>
      )}

      <p className="text-xs text-ink-mute italic mt-10 text-center">
        Cancel any time in Settings, and you keep access to the end of the period you’ve paid for. Your entries are always yours to keep.
      </p>
    </div>
  );
}

function PlanCard({ plan, onSelect, loading, disabled }) {
  const isRecommended = plan.recommended;
  return (
    <motion.button
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: isRecommended ? 0 : 0.1 }}
      onClick={onSelect}
      disabled={disabled}
      data-testid={`plan-${plan.key}`}
      className={`relative text-left rounded-3xl p-8 border transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
        isRecommended
          ? "bg-clay text-canvas border-clay hover:bg-clay-dark"
          : "bg-canvas-warm text-ink border-line hover:border-line-strong"
      }`}
    >
      {isRecommended && (
        <span className="absolute -top-3 left-8 text-[10px] uppercase tracking-[0.3em] bg-ink text-canvas px-3 py-1 rounded-full">
          Recommended
        </span>
      )}
      <p className={`text-xs uppercase tracking-[0.25em] mb-4 ${isRecommended ? "text-canvas/70" : "text-ink-mute"}`}>
        {plan.label}
      </p>
      <div className="flex items-baseline gap-2 mb-2">
        {plan.was && (
          <span className={`font-serif-display text-2xl line-through ${isRecommended ? "text-canvas/50" : "text-ink-mute"}`}>
            ${plan.was.toFixed(0)}
          </span>
        )}
        <span className="font-serif-display text-5xl">${plan.amount.toFixed(plan.amount % 1 === 0 ? 0 : 2)}</span>
        <span className={`text-sm ${isRecommended ? "text-canvas/70" : "text-ink-mute"}`}>
          / {plan.key === "annual" ? "year" : "month"}
        </span>
      </div>
      <p className={`text-sm mb-2 ${isRecommended ? "text-canvas/85" : "text-ink-soft"}`}>{plan.note}</p>
      <p className={`text-xs mb-6 ${isRecommended ? "text-canvas/70" : "text-ink-mute"}`}>
        14 days free first · then billed automatically
      </p>
      <span className={`inline-flex items-center gap-2 text-sm font-medium ${isRecommended ? "text-canvas" : "text-clay"}`}>
        {loading ? "Opening…" : "Choose this"}
        <ArrowRight size={14} weight="bold" />
      </span>
    </motion.button>
  );
}
